import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PaginatedLeadsDto } from '@api/apiCompat';
import { useDynamicColumns } from '@hooks/common/useDynamicColumns/useDynamicColumns';
import NoLeadsAvailableScreen from '@pages/CampaignDetails/components/Leads/components/NoLeadsAvailableScreen';
import { Page } from '@pages/LeadScrapers/types';

import { LinearProgress, SxProps } from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';

import { StyledDataGrid } from './styled';

// Number of preview rows to show unblurred on each page
const PREVIEW_ROW_COUNT = 3;

const ScrapekingDbDynamicDataGrid: FC<{
  handlePageChange: (newPaginationModel: GridPaginationModel) => void;
  paginationModel: Page;
  isDataLoading: boolean;
  data?: PaginatedLeadsDto;
  isReadOnly?: boolean;
  sx?: SxProps;
  expandedFilters?: boolean;
  campaignType?: string;
  handleCheckboxSelection: (selectionModel: GridRowSelectionModel) => void;
  selectedLeadIds?: GridRowSelectionModel;
  onUnlockLead?: (leadId: string) => Promise<{ success: boolean; error?: string }>;
  /** Set to true for My Leads tab - all rows are purchased, no blur needed */
  isMyLeads?: boolean;
}> = ({
  data,
  handlePageChange,
  paginationModel,
  isDataLoading,
  sx,
  campaignType,
  expandedFilters,
  handleCheckboxSelection,
  selectedLeadIds,
  onUnlockLead,
  isMyLeads = false,
}) => {
  const { dbColumns, onRowClick } = useDynamicColumns({ campaignType });
  const { t } = useTranslation();

  // Add rowIndex to each row for determining which rows should show preview (unblurred) data
  // For My Leads: all rows are purchased, so mark them as such (no blur)
  const rowsWithIndex = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((row, index) => ({
      ...row,
      _rowIndex: index,
      // For My Leads: all rows are purchased, no preview needed
      // For ScrapeKing DB: first 3 rows are preview, rest need purchase
      _isPreviewRow: isMyLeads ? true : index < PREVIEW_ROW_COUNT,
      // Mark as purchased for My Leads so blur logic doesn't apply
      purchased: isMyLeads ? true : row.purchased,
      is_purchased: isMyLeads ? true : row.is_purchased,
      _onUnlockLead: onUnlockLead,
    }));
  }, [data?.data, onUnlockLead, isMyLeads]);

  const props = useMemo(
    () => ({
      rowCount: data?.total,
      loading: isDataLoading,
      pagination: !!data?.total || true,
      rows: rowsWithIndex,
      onRowClick,
      columns: dbColumns,
    }),
    [data?.total, rowsWithIndex, isDataLoading, onRowClick, dbColumns],
  );

  return (
    <StyledDataGrid
      autoHeight
      checkboxSelection
      disableColumnFilter
      disableRowSelectionOnClick
      disableColumnSelector
      disableColumnMenu
      paginationMode="server"
      showCellVerticalBorder={false}
      pageSizeOptions={[25, 50, 100]}
      onPaginationModelChange={handlePageChange}
      paginationModel={paginationModel}
      slots={{
        loadingOverlay: LinearProgress,
        noRowsOverlay: NoLeadsAvailableScreen,
      }}
      slotProps={{
        pagination: {
          labelRowsPerPage: t('common.paginationPerPageText'),
        },
      }}
      sx={{
        ...sx,
      }}
      getRowId={row => row.leads_id}
      expandedFilters={expandedFilters}
      onRowSelectionModelChange={handleCheckboxSelection}
      rowSelectionModel={selectedLeadIds}
      getRowClassName={params => (params.row.purchased ? 'purchased' : '')}
      {...props}
    />
  );
};

export default memo(ScrapekingDbDynamicDataGrid);
